// CSV builder for the meetings report. All filtering (role/type/status/kupah/months +
// mode + therapists) lives in the shared panel at the top of the explorer; this helper
// receives the granular rows + the panel state and downloads the CSV. Always split intake/מעקב.

export type GranRow = { therapist: string; role: string; month: string; kind: string; status: string; clinic: string; n: number; minutes: number };

const HE_MON = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const monthLabel = (id: string) => { const [y, m] = id.split('-'); return `${HE_MON[+m - 1]} ${y}`; };
const KIND_LABEL: Record<string, string> = {
  intake: 'אינטייק', followup: 'מעקב', double: 'מעקב כפול', reintake: 'ריאינטייק',
};
const sumVals = (o: Record<string, number>) => Object.values(o).reduce((a, b) => a + b, 0);

export function reportFiltered(rows: GranRow[], mode: 'summary' | 'detailed', f: {
  roles: Set<string>; kinds: Set<string>; statuses: Set<string>; clinics: Set<string>; therapists: Set<string>;
}): GranRow[] {
  return rows.filter((r) =>
    f.roles.has(r.role) && f.kinds.has(r.kind) && f.statuses.has(r.status) && f.clinics.has(r.clinic)
    && (mode === 'summary' || f.therapists.has(r.therapist)));
}

export function buildReportCsv(rows: GranRow[], months: string[], mode: 'summary' | 'detailed', f: {
  roles: Set<string>; kinds: Set<string>; statuses: Set<string>; clinics: Set<string>; therapists: Set<string>;
}) {
  const filtered = reportFiltered(rows, mode, f);
  // With a date range and no month ticked there is no picker to read the columns from,
  // so they come from the rows themselves — the months the data actually landed in.
  const ms = (months.length ? [...months] : Array.from(new Set(rows.map((r) => r.month)))).sort();
  // Four disjoint kinds since 2026-07-26 (was intake/followup) — see MeetingsExplorer.
  const selKinds = ['intake', 'followup', 'double', 'reintake'].filter((k) => f.kinds.has(k));
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines: string[][] = [];

  if (mode === 'summary') {
    const measure = (title: string, val: (r: GranRow) => number, div: number, fmt: (x: number) => string) => {
      lines.push([title]);
      lines.push(['', ...ms.map(monthLabel), 'סה"כ']);
      const totByMonth: Record<string, number> = {};
      for (const k of selKinds) {
        const colTot: Record<string, number> = {};
        for (const r of filtered) if (r.kind === k) colTot[r.month] = (colTot[r.month] ?? 0) + val(r);
        const rowTot = ms.reduce((a, m) => a + (colTot[m] ?? 0), 0);
        lines.push([KIND_LABEL[k], ...ms.map((m) => fmt((colTot[m] ?? 0) / div)), fmt(rowTot / div)]);
        for (const m of ms) totByMonth[m] = (totByMonth[m] ?? 0) + (colTot[m] ?? 0);
      }
      const grand = ms.reduce((a, m) => a + (totByMonth[m] ?? 0), 0);
      lines.push(['סה"כ', ...ms.map((m) => fmt((totByMonth[m] ?? 0) / div)), fmt(grand / div)]);
      lines.push([]);
    };
    measure('סיכום — מספר פגישות', (r) => r.n, 1, (x) => String(x));
    measure('סיכום — שעות', (r) => r.minutes, 60, (x) => String(Math.round(x * 10) / 10));
  } else {
    for (const k of selKinds) {
      lines.push([`פגישות ${KIND_LABEL[k]} — לפי רופא`]);
      lines.push(['שם הרופא', ...ms.map(monthLabel), 'סה"כ']);
      const byTh: Record<string, Record<string, number>> = {};
      for (const r of filtered) if (r.kind === k) (byTh[r.therapist] ??= {})[r.month] = (byTh[r.therapist][r.month] ?? 0) + r.n;
      const order = Object.keys(byTh).sort((a, b) => sumVals(byTh[b]) - sumVals(byTh[a]));
      const colTot: Record<string, number> = {};
      for (const th of order) {
        let rowTot = 0;
        const cells = ms.map((m) => { const v = byTh[th][m] ?? 0; rowTot += v; colTot[m] = (colTot[m] ?? 0) + v; return String(v); });
        lines.push([th, ...cells, String(rowTot)]);
      }
      const grand = ms.reduce((a, m) => a + (colTot[m] ?? 0), 0);
      lines.push(['סה"כ', ...ms.map((m) => String(colTot[m] ?? 0)), String(grand)]);
      lines.push([]);
    }
  }

  const csv = '﻿' + lines.map((row) => row.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `דוח_פגישות_${ms[0] ?? ''}_${ms[ms.length - 1] ?? ''}.csv`; a.click();
  URL.revokeObjectURL(url);
}
